import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { I18NextModule } from 'angular-i18next';
import { CarDetailModalComponent } from '../car-detail-modal/car-detail-modal.component';
import { CarLegalModalComponent } from '../car-legal-modal/car-legal-modal.component';
import { MatIconModule } from '@angular/material/icon';
import { SharedModule } from '../../shared.module';
import { CampaignTypes } from '../../model/campaign.enum';
import { CarEquipmentModalComponent } from '../car-equipment-modal/car-equipment-modal.component';
import { RequestType } from '../../model/requestType.enum';
import { ImageModalComponent } from '../image-modal/image-modal.component';

@Component({
  standalone: true,
  selector: 'app-car-card',
  templateUrl: './car-card.component.html',
  styleUrls: ['./car-card.component.scss'],
  imports: [CommonModule, SharedModule, I18NextModule, MatIconModule]
})
export class CarCardComponent {
  @Input() spinner: boolean | undefined;
  @Input() stock: any;
  @Input() requestType!: string;
  @Input() campaignType!: string;
  @Input() isBookedList = false;
  @Input() viewMobile = false;
  @Output() requestCar = new EventEmitter<any>();
  @Output() onBack = new EventEmitter<void>();
  @Output() carListChange = new EventEmitter<any[]>();
  @Output() cancelBooking = new EventEmitter<string>();

  selectedCars: { car: any; order: number }[] = [];
  carList: any[] = [];
  campaignTypeEnum = CampaignTypes;
  maxSelections = 3;
  requestTypeEnum = RequestType;
  activeImageIndices: { [key: number]: number } = {};

  constructor(public dialog: MatDialog) {}

  getImgIndex(carIndex: number): number {
    return this.activeImageIndices[carIndex] || 0;
  }

  nextImg(carIndex: number, total: number, event: Event): void {
    event.stopPropagation();
    const current = this.getImgIndex(carIndex);
    this.activeImageIndices[carIndex] = (current + 1) % total;
  }

  prevImg(carIndex: number, total: number, event: Event): void {
    event.stopPropagation();
    const current = this.getImgIndex(carIndex);
    this.activeImageIndices[carIndex] = (current - 1 + total) % total;
  }

  openImageCarousel(images: string[], selectedIndex: number): void {
    this.dialog.open(ImageModalComponent, {
      data: { images, selectedIndex },
      panelClass: 'image-dialog',
      backdropClass: 'dark-backdrop'
    });
  }

  openCarDetail(car: any): void {
    const dialogRef = this.dialog.open(CarDetailModalComponent, {
      width: '1490px',
      panelClass: 'seat-modal',
      data: {
        car,
        requestType: this.requestType,
        theme: 'light',
        view: this.viewMobile
      }
    });

    dialogRef.componentInstance.onReserve.subscribe((carToReserve: any) => {
      dialogRef.close();
      this.requestCarButton(carToReserve);
    });
  }

  openCarLegal(car: any): void {
    this.dialog.open(CarLegalModalComponent, {
      width: '1110px',
      panelClass: 'seat-modal',
      data: {
        car,
        theme: 'light'
      }
    });
  }

  requestCarButton(car: any): void {
    this.requestCar.emit(car);
  }

  back(): void {
    this.onBack.emit();
  }

  selectCar(car: any) {
    if (this.campaignType === this.campaignTypeEnum.STANDARD) {
      if (this.selectedCars.length > 0) {
        const index = this.selectedCars.findIndex((item) => item.car === car);
        if (index !== -1) {
          this.selectedCars.splice(index, 1);
        } else {
          this.selectedCars.splice(index, 1);
          this.selectedCars.push({ car, order: 1 });
        }
      } else {
        this.selectedCars.push({ car, order: 1 });
      }
    } else {
      const index = this.selectedCars.findIndex((item) => item.car === car);

      if (index !== -1) {
        this.selectedCars.splice(index, 1);
        this.reorderSelectedCars();
      } else if (this.selectedCars.length < this.maxSelections) {
        this.selectedCars.push({
          car,
          order: this.selectedCars.length + 1
        });
      }
    }

    this.carList = this.selectedCars.map((item) => item.car);
  }

  nextStep() {
    this.carListChange.emit(this.carList);
  }

  reorderSelectedCars() {
    this.selectedCars = this.selectedCars.map((item, index) => ({
      car: item.car,
      order: index + 1
    }));
  }

  isSelected(car: any): boolean {
    return this.selectedCars.some((item) => item.car === car);
  }

  getCarOrder(car: any): number | null {
    const selectedCar = this.selectedCars.find((item) => item.car === car);
    return selectedCar ? selectedCar.order : null;
  }

  openCarEquipment(car: any): void {
    let equipment = car.optionals;
    if (!equipment) equipment = car.extensions;

    if (!equipment) return;

    this.dialog.open(CarEquipmentModalComponent, {
      width: '800px',
      panelClass: 'seat-modal',
      data: {
        equipment,
        theme: 'light'
      }
    });
  }
}
